import type {
  BusinessFunction,
  CreateBusinessFunctionBody,
  UpdateBusinessFunctionBody,
} from '@lib/api-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient, unwrap } from '../api/client.ts';

/**
 * View-model hooks for the business_functions resource.
 *
 * Per the architecture plan §"Frontend view models", views import
 * from here rather than calling the API directly. Mutations live
 * next to the read hook they primarily affect with explicit
 * invalidation lists.
 */

export interface ListBusinessFunctionsOptions {
  atEffective?: number;
  atAuthored?: number;
  branchId?: number;
}

const KEYS = {
  all: (slug: string) => ['business_functions', slug] as const,
  list: (slug: string, opts: ListBusinessFunctionsOptions) =>
    ['business_functions', slug, 'list', opts] as const,
};

function listQuery(opts: ListBusinessFunctionsOptions) {
  const q: Record<string, string> = {};
  if (opts.atEffective !== undefined) q.at_effective = String(opts.atEffective);
  if (opts.atAuthored !== undefined) q.at_authored = String(opts.atAuthored);
  if (opts.branchId !== undefined) q.branch_id = String(opts.branchId);
  return q;
}

interface ListResponseBody {
  object: 'list';
  url: string;
  has_more: boolean;
  data: BusinessFunction[];
}

interface SingleResponseBody {
  business_function: BusinessFunction;
}

interface DeleteResponseBody {
  deleted: true;
  id: string;
}

export function useBusinessFunctions(
  orgSlug: string | null,
  opts: ListBusinessFunctionsOptions = {},
) {
  return useQuery({
    queryKey: orgSlug
      ? KEYS.list(orgSlug, opts)
      : ['business_functions', null, 'list'],
    queryFn: async () => {
      if (!orgSlug) throw new Error('useBusinessFunctions called without orgSlug');
      const res = await apiClient.v1.orgs[':slug'].business_functions.$get({
        param: { slug: orgSlug },
        query: listQuery(opts),
      });
      const body = await unwrap<ListResponseBody>(res);
      return body.data;
    },
    enabled: !!orgSlug,
    staleTime: 30_000,
  });
}

export function useBusinessFunction(
  orgSlug: string | null,
  id: string | null,
  opts: ListBusinessFunctionsOptions = {},
) {
  return useQuery({
    queryKey: orgSlug && id
      ? (['business_functions', orgSlug, 'one', id, opts] as const)
      : (['business_functions', null, 'one'] as const),
    queryFn: async () => {
      if (!orgSlug || !id) throw new Error('useBusinessFunction called without orgSlug+id');
      const res = await apiClient.v1.orgs[':slug'].business_functions[':id'].$get({
        param: { slug: orgSlug, id },
        query: listQuery(opts),
      });
      const body = await unwrap<SingleResponseBody>(res);
      return body.business_function;
    },
    enabled: !!orgSlug && !!id,
    staleTime: 30_000,
  });
}

export function useCreateBusinessFunction(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateBusinessFunctionBody) => {
      const res = await apiClient.v1.orgs[':slug'].business_functions.$post({
        param: { slug: orgSlug },
        json: body,
      });
      const data = await unwrap<SingleResponseBody>(res);
      return data.business_function;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.all(orgSlug) });
    },
  });
}

export function useUpdateBusinessFunction(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; body: UpdateBusinessFunctionBody }) => {
      const res = await apiClient.v1.orgs[':slug'].business_functions[':id'].$patch({
        param: { slug: orgSlug, id: args.id },
        json: args.body,
      });
      const data = await unwrap<SingleResponseBody>(res);
      return data.business_function;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.all(orgSlug) });
    },
  });
}

export function useDeleteBusinessFunction(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      // DELETE has no body — `effective_at` (when needed) lands as a
      // future query param. Today the endpoint defaults effective_at
      // to now.
      const res = await apiClient.v1.orgs[':slug'].business_functions[':id'].$delete({
        param: { slug: orgSlug, id },
      });
      return unwrap<DeleteResponseBody>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.all(orgSlug) });
    },
  });
}
